import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';
import getAuthToken from '../utils/cookie';
import { downloadSubmissionDataApi } from '../api/submissions-database';
import downloadDocument from '../utils/downloadDocument';
import Progress from '../components/atoms/Progress/Progress';
import { CheckCircledIcon, CrossCircledIcon, ProcessingIconDashed } from '../components/icons/icons';
import { SubmissionsContext } from '../context/submissions';
import ReportDetailMetadata from '../components/report-detail/ReportDetailMetadata';
import SingleEntryField from '../components/submissions-database/SingleEntryField';
import DocPermissionEnum from 'enums/DocPermissionEnum';
import characterImageUrl from '../utils/characterImageUrl';
import parseDate from '../utils/parseDate';

const POLL_INTERVAL_MS = 5000;

const getSteps = (isBook) => [
  {
    key: 'long_synopsis',
    label: isBook ? 'Manuscript Analysis' : 'Analysis',
    description: isBook
      ? 'Reading and summarizing the full manuscript'
      : 'Reading and summarizing the full script',
  },
  {
    key: 'analysis_complete',
    label: 'Refining & Extracting',
    description: isBook
      ? 'Extracting characters, comps, and key details'
      : 'Extracting characters, comps, and key details',
  },
  {
    key: 'images_complete',
    label: 'Character Portraits',
    description: 'Generating character images',
  },
];

const STEP_ORDER = getSteps(false).map((s) => s.key);

function stepIndexFromResponse(status, currentStep) {
  if (status === 'COMPLETED') return STEP_ORDER.length;
  if (!currentStep) return 0;
  const idx = STEP_ORDER.indexOf(currentStep);
  return idx === -1 ? 0 : idx + 1;
}

function SectionCard({ gradientClass, title, titleClass, children }) {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className="rounded-[10px] bg-surface border border-surface-border overflow-hidden">
      <div className={`h-[3px] bg-gradient-to-r ${gradientClass}`} />
      <div 
        className="p-4 border-b border-surface-border flex justify-between items-center cursor-pointer hover:bg-gray-800/40 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3 className={`font-semibold text-base ${titleClass}`}>{title}</h3>
        <span className="text-gray-400">
          {isExpanded ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path></svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
          )}
        </span>
      </div>
      {isExpanded && <div className="p-5">{children}</div>}
    </div>
  );
}

function CompColumn({ label, items }) {
  if (!items?.length) return null;
  return (
    <div className="bg-gray-800/80 p-4 rounded-xl border border-gray-700/50">
      <span className="text-[10px] text-gray-400 font-bold uppercase block mb-2">{label}</span>
      <ul className="list-disc pl-4 text-sm text-gray-300 space-y-2">
        {items.map((t, idx) => (
          <li key={idx}>
            <span className="font-bold text-white">{typeof t === 'string' ? t : t.title}</span>
            {typeof t === 'object' && t.year && (
              <span className="text-gray-400 ml-1">({t.year})</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function CharacterImage({ char, showPlaceholder }) {
  const [broken, setBroken] = useState(false);
  const src = characterImageUrl(char);

  if (src && !broken) {
    return (
      <img
        src={src}
        alt={char.name || 'Character'}
        className="w-full h-auto rounded-lg shadow-md border border-gray-700 object-cover min-h-[120px]"
        onError={() => setBroken(true)}
      />
    );
  }

  if (showPlaceholder) {
    return (
      <div className="w-full min-h-[120px] rounded-lg border border-gray-700 bg-gray-900/60 flex items-center justify-center">
        <ProcessingIconDashed className="spin text-orange-400 w-8 h-8" />
      </div>
    );
  }

  return null;
}

function StepRow({ step, stepsComplete, isActive }) {
  const done = stepsComplete > STEP_ORDER.indexOf(step.key);
  return (
    <div className="flex items-start gap-3 py-3">
      <div className="mt-0.5 flex-shrink-0">
        {done ? (
          <CheckCircledIcon className="text-green-400" />
        ) : isActive ? (
          <ProcessingIconDashed className="spin text-orange-400" />
        ) : (
          <div className="w-4 h-4 rounded-full border border-[#444] mt-0.5" />
        )}
      </div>
      <div>
        <p
          className={`text-sm font-medium ${
            done ? 'text-white' : isActive ? 'text-orange-300' : 'text-[#666]'
          }`}>
          {step.label}
        </p>
        <p className="text-xs text-[#555] mt-0.5">{step.description}</p>
      </div>
    </div>
  );
}

/** Pipeline-generated sections only (no logline/genre — those live in metadata when complete). */
function ReportSections({ report, isProcessing }) {
  const films = report?.similar_films || [];
  const books = report?.similar_books || [];
  const series = report?.similar_tv_series || [];
  const hasComps = films.length > 0 || books.length > 0 || series.length > 0;
  const showImagePlaceholder =
    isProcessing && report?.current_step === 'images_complete';

  return (
    <div className="flex flex-col gap-6 mt-8">
      {report?.summary && (
        <SectionCard
          gradientClass="from-blue-500 to-purple-500"
          title="Long Synopsis"
          titleClass="text-purple-300">
          <div className="whitespace-pre-wrap font-serif text-base text-gray-200 leading-relaxed">
            {report.summary}
          </div>
        </SectionCard>
      )}

      {report?.short_synopsis && (
        <SectionCard
          gradientClass="from-emerald-500 to-green-500"
          title="Short Synopsis"
          titleClass="text-emerald-300">
          <div className="whitespace-pre-wrap font-serif text-base text-gray-200 leading-relaxed">
            {report.short_synopsis}
          </div>
        </SectionCard>
      )}

      {report?.meta_adaptability && (
        <SectionCard
          gradientClass="from-cyan-500 to-blue-500"
          title="Adaptability"
          titleClass="text-cyan-300">
          <div className="whitespace-pre-wrap font-serif text-base text-gray-200 leading-relaxed">
            {report.meta_adaptability}
          </div>
        </SectionCard>
      )}

      {report?.demographics?.age_range && (
        <SectionCard
          gradientClass="from-orange-500 to-pink-500"
          title="Demographics"
          titleClass="text-orange-300">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <h4 className="text-[10px] font-bold text-orange-400 uppercase tracking-wider mb-1">
                Age Range
              </h4>
              <p className="text-gray-200 text-sm">{report.demographics.age_range || '-'}</p>
            </div>
            <div>
              <h4 className="text-[10px] font-bold text-orange-400 uppercase tracking-wider mb-1">
                Gender
              </h4>
              <p className="text-gray-200 text-sm">{report.demographics.gender || '-'}</p>
            </div>
            <div>
              <h4 className="text-[10px] font-bold text-orange-400 uppercase tracking-wider mb-1">
                Status
              </h4>
              <p className="text-gray-200 text-sm">
                {report.demographics.socioeconomic_status || '-'}
              </p>
            </div>
            <div>
              <h4 className="text-[10px] font-bold text-orange-400 uppercase tracking-wider mb-1">
                Habits
              </h4>
              <p className="text-gray-200 text-sm">{report.demographics.viewing_habits || '-'}</p>
            </div>
          </div>
          {report.demographics.interests && (
            <div className="mt-4">
              <h4 className="text-[10px] font-bold text-orange-400 uppercase tracking-wider mb-1">
                Interests
              </h4>
              <p className="text-gray-200 text-sm">{report.demographics.interests}</p>
            </div>
          )}
        </SectionCard>
      )}

      {hasComps && (
        <SectionCard
          gradientClass="from-pink-500 to-rose-500"
          title="Comparable Titles"
          titleClass="text-pink-300">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <CompColumn label="Films" items={films} />
            <CompColumn label="Series" items={series} />
            <CompColumn label="Books" items={books} />
          </div>
        </SectionCard>
      )}

      {report?.characters?.length > 0 && (
        <SectionCard
          gradientClass="from-yellow-500 to-amber-500"
          title={`Character Breakdowns (${report.characters.length})`}
          titleClass="text-yellow-500">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {report.characters.map((char, index) => {
              const imgSrc = characterImageUrl(char);
              const ageLabel = char.age_range || char.age;
              return (
                <div
                  key={index}
                  className="bg-gray-800/80 rounded-xl p-5 border border-gray-700/50 flex flex-col xl:flex-row gap-5">
                  {(imgSrc || showImagePlaceholder) && (
                    <div className="xl:w-1/3 flex-shrink-0">
                      <CharacterImage
                        char={char}
                        showPlaceholder={showImagePlaceholder && !imgSrc}
                      />
                    </div>
                  )}
                  <div className={imgSrc || showImagePlaceholder ? 'xl:w-2/3' : 'w-full'}>
                    <div className="flex items-end justify-between mb-3 border-b border-gray-700 pb-2">
                      <h4 className="text-lg font-bold text-gray-100">{char.name}</h4>
                      <span className="text-[10px] font-mono text-gray-400 uppercase font-bold tracking-wider">
                        {char.gender}
                        {char.gender && ageLabel ? ' • ' : ''}
                        {ageLabel}
                      </span>
                    </div>
                    {(char.description || char.description_coverage) && (
                      <div className="text-sm text-gray-300 mb-4 leading-relaxed bg-gray-900/60 p-3 rounded-lg">
                        <span className="text-[10px] font-bold text-blue-400 uppercase tracking-wider block mb-1">
                          Description
                        </span>
                        {char.description || char.description_coverage}
                      </div>
                    )}
                    {char.character_arc && (
                      <div>
                        <span className="text-[10px] font-bold text-amber-500 uppercase tracking-wider block mb-1">
                          Narrative Arc
                        </span>
                        <p className="text-sm text-gray-400 font-serif italic">{char.character_arc}</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}
    </div>
  );
}

export default function ReportDetailPage() {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [editingField, setEditingField] = useState({ field: '', itemId: '' });
  const intervalRef = useRef(null);
  // null until first poll response sets it from report.created_at
  const startTimeRef = useRef(null);
  const fakeTimerRef = useRef(null);
  const [displayProgress, setDisplayProgress] = useState(0);

  // Derived from report state — safe to compute early (report is null before first fetch)
  const status = report?.status || 'PROCESSING';
  const isFailed = status === 'FAILED';
  const stepsComplete = stepIndexFromResponse(status, report?.current_step);
  const progressValue = Math.round((stepsComplete / STEP_ORDER.length) * 100);

  const fetchProgress = async () => {
    try {
      const tok = getAuthToken();
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}submission/progress/${submissionId}/`,
        { headers: { Authorization: `Bearer ${tok}` } }
      );
      const data = { ...res.data, _id: res.data._id || submissionId };
      setReport(data);

      // Anchor fake progress clock to the report's actual creation time (once).
      // This survives navigation — elapsed is always relative to when the job started.
      if (data.created_at && startTimeRef.current === null) {
        startTimeRef.current = parseDate(data.created_at).getTime();
      }

      const done = ['COMPLETED', 'FAILED'].includes(data?.status);
      if (done && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error('Report not found.');
        navigate('/material-log');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProgress();
    intervalRef.current = setInterval(fetchProgress, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [submissionId]);

  useEffect(() => {
    const FAKE_DURATION_MS = 120_000; // 2 minutes
    const FAKE_MAX = 25;             // cap at 25% while faking

    // Stop and reset on failure — progress bar is hidden but timer must not leak
    if (isFailed) {
      if (fakeTimerRef.current) {
        clearInterval(fakeTimerRef.current);
        fakeTimerRef.current = null;
      }
      setDisplayProgress(0);
      return;
    }

    if (stepsComplete > 0) {
      // Real step done — clear fake timer and show real value
      if (fakeTimerRef.current) {
        clearInterval(fakeTimerRef.current);
        fakeTimerRef.current = null;
      }
      setDisplayProgress(progressValue);
      return;
    }

    const tick = () => {
      // Fall back to Date.now() only if created_at hasn't arrived yet
      const start = startTimeRef.current ?? Date.now();
      const elapsed = Math.max(0, Date.now() - start);
      const ratio = Math.min(elapsed / FAKE_DURATION_MS, 1);
      // Ease-out quadratic: fast start, slows as it approaches 25%
      const eased = 1 - Math.pow(1 - ratio, 2);
      setDisplayProgress(Math.round(eased * FAKE_MAX));
    };

    tick(); // run immediately on mount
    fakeTimerRef.current = setInterval(tick, 2000);

    return () => {
      if (fakeTimerRef.current) {
        clearInterval(fakeTimerRef.current);
        fakeTimerRef.current = null;
      }
    };
  }, [isFailed, stepsComplete, progressValue]);

  const detailContextValue = useMemo(() => {
    const row = report ? { ...report, _id: report._id || submissionId } : null;
    return {
      submissionsData: row ? [row] : [],
      setSubmissionData: (fn) => {
        setReport((prev) => {
          if (!prev) return prev;
          const rows = fn([{ ...prev, _id: prev._id || submissionId }]);
          const updated = rows.find((r) => String(r._id) === String(submissionId));
          return updated ? { ...prev, ...updated, _id: submissionId } : prev;
        });
      },
      hiddenColumns: [],
      filters: {},
      setFilters: () => {},
      updateFilters: () => {},
      loading: false,
      setLoading: () => {},
      filteredData: [],
      fetchMoreSubmissions: () => {},
      hasMore: false,
      resetSubmissionData: () => {},
      fetchSubmissions: () => {},
      searchText: '',
      setSearchText: () => {},
      sortingCriteria: { sortBy: '', sortOrder: 1 },
      setSortingCriteria: () => {},
      fetchAndSetFilterOption: () => {},
      filtersOptions: {}
    };
  }, [report, submissionId]);

  const handleDownload = async () => {
    const reportTitle = report?.title || 'Report';
    setIsDownloading(true);
    const toastId = toast.loading(`'${reportTitle}' downloading...`, { duration: Infinity });
    try {
      const response = await downloadSubmissionDataApi({ submissionId });
      const { filename } = response.headers;
      downloadDocument(response.data, filename);
      toast.success(`'${reportTitle}' downloaded.`, { id: toastId, duration: 3000 });
    } catch {
      toast.error(`Failed to download '${reportTitle}'.`, { id: toastId, duration: 3000 });
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <ProcessingIconDashed className="spin text-orange-400" />
      </div>
    );
  }

  const isProcessing = status === 'PROCESSING';
  const isCompleted = status === 'COMPLETED';
  // isFailed is hoisted above hooks — no re-declaration needed
  const showMetadata = isCompleted || isFailed;
  const canEditTitle = report?.actions?.includes(DocPermissionEnum.DOC_EDIT);
  const item = report ? { ...report, _id: report._id || submissionId } : null;

  return (
    <SubmissionsContext.Provider value={detailContextValue}>
      <div className="max-w-4xl mx-auto py-10 px-4">
        <button
          type="button"
          onClick={() => navigate('/material-log')}
          className="text-xs text-[#888] hover:text-white mb-6 flex items-center gap-1 transition-colors">
          ← Back to Material Log
        </button>

        <div className="mb-2 flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-[#666] font-medium">
            {report?.type || ''}
          </span>
        </div>

        {isProcessing && item ? (
          <div className="mb-1">
            <SingleEntryField
              defaultValue={item.title}
              fieldName="title"
              item={item}
              editingField={editingField}
              setEditingField={setEditingField}
              fieldWidth="100%"
              canEdit={canEditTitle}
            />
          </div>
        ) : (
          <h1 className="text-2xl font-bold text-white mb-1 truncate">
            {report?.title || 'Untitled Report'}
          </h1>
        )}

        <p className="text-xs text-[#666] mb-8">
          {report?.created_at ? `Started ${new Date(report.created_at).toLocaleString()}` : ''}
        </p>

        {showMetadata && <ReportDetailMetadata report={report} />}

        {isFailed ? (
          <div className="flex flex-col gap-3 mb-6">
            <div className="flex items-start gap-3 p-4 rounded-lg bg-red-900/20 border border-red-800/40">
              <CrossCircledIcon className="text-red-400 mt-0.5 flex-shrink-0 opacity-70" />
              <div>
                <p className="text-sm font-medium text-red-300">Report generation failed</p>
                {report?.error_message && (
                  <p className="text-xs text-red-400/70 mt-1">{report.error_message}</p>
                )}
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 rounded-lg bg-orange-900/10 border border-orange-800/30">
              <svg className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-orange-300">Your credit has been returned</p>
                <p className="text-xs text-orange-400/70 mt-1">
                  No charge was applied. The credit for this report has been automatically restored to your account.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {isProcessing && (
              <div className="mb-6">
                <div className="flex justify-between text-xs text-[#666] mb-2">
                  <span>
                    {stepsComplete === 0
                      ? 'Preparing...'
                      : `Step ${stepsComplete} of ${STEP_ORDER.length} complete`}
                  </span>
                  <span>{displayProgress}%</span>
                </div>
                <Progress value={displayProgress} striped />
              </div>
            )}

            {isProcessing && (
              <div className="border border-[#1D2939] rounded-lg divide-y divide-[#1D2939] mb-8">
                {getSteps(report?.type === 'BOOK').map((step, idx) => (
                  <div key={step.key} className="px-4">
                    <StepRow
                      step={step}
                      stepsComplete={stepsComplete}
                      isActive={idx === stepsComplete}
                    />
                  </div>
                ))}
              </div>
            )}

            <ReportSections report={report} isProcessing={isProcessing} />
          </>
        )}

        {isCompleted && (
          <button
            type="button"
            onClick={handleDownload}
            disabled={isDownloading}
            className="w-full py-3 rounded-lg text-sm font-medium bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-500 hover:to-orange-400 text-white transition-all duration-200 disabled:opacity-50 mt-8">
            {isDownloading ? 'Downloading...' : 'Download Report (.docx)'}
          </button>
        )}
      </div>
    </SubmissionsContext.Provider>
  );
}
